"use client";

import React from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../dialog.js";
import { Button } from "../../button.js";
import { Alert, AlertDescription } from "../../alert.js";
import { Calendar, CheckCircle, Video } from "lucide-react";
import { MeetingFormData } from "../types/meeting.js";
import { getPlatformShortLabel } from "../constants/meetingConstants.js";

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: MeetingFormData;
  responseData: any;
  currentAttendees: string[];
}

export const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  onClose,
  formData,
  responseData,
  currentAttendees,
}) => {
  const meetingLink =
    responseData?.conferenceData?.entryPoints?.[0]?.uri;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-card border border-gray-200 shadow-xl">
        <DialogHeader className="pb-6">
          <DialogTitle className="flex items-center gap-3 text-2xl font-semibold text-slate-900">
            <div className="p-2 bg-blue-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-blue-600" />
            </div>
            Meeting Created Successfully!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Alert className="bg-blue-50 border-blue-200">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <AlertDescription className="text-slate-800">
              <div className="space-y-2">
                <p>
                  ✅ Meeting successfully created in{" "}
                  {getPlatformShortLabel(formData.platform)} Calendar
                </p>
                <p>✅ Calendar invitations sent to all attendees</p>
                {formData.isOnline && (
                  <p>✅ Online meeting link generated and included</p>
                )}
                {currentAttendees.length > 0 && (
                  <p>
                    ✅ {currentAttendees.length} attendee
                    {currentAttendees.length !== 1 ? "s" : ""} notified
                  </p>
                )}
              </div>
            </AlertDescription>
          </Alert>

          {responseData && (
            <div className="p-4 bg-card rounded-lg border border-gray-200 shadow-sm">
              <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Meeting Details
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Title:</span>
                  <span className="text-card-foreground">
                    {responseData.summary || responseData.subject}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Start:</span>
                  <span className="text-card-foreground">
                    {format(
                      new Date(
                        responseData.start?.dateTime ||
                          responseData.start?.dateTime
                      ),
                      "PPP 'at' p"
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Duration:</span>
                  <span className="text-card-foreground">
                    {formData.duration} minutes
                  </span>
                </div>
                {meetingLink && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">
                        Meeting Link:
                      </span>
                      <a
                        href={meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        Join Meeting
                      </a>
                    </div>
                  </div>
                )}
                {formData.attendees && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-700">
                        Attendees:
                      </span>
                      <span className="text-card-foreground">
                        {currentAttendees.length} people
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={onClose}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-colors"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Done
            </Button>
            {meetingLink && (
              <Button
                onClick={() => window.open(meetingLink, "_blank")}
                variant="outline"
                className="border-gray-300 text-slate-700 hover:bg-card-50 hover:border-gray-400 transition-colors"
              >
                <Video className="h-4 w-4 mr-2" />
                Join Now
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
